import { TagsIcon } from "lucide-react";

import { gql } from "@apollo/client";

import { TagColorEnum, TagFragment } from "@/__generated__/graphql";

import { FieldEnum } from "@/components/organisms/Field/FieldEnum";
import { Mutations } from "@/constants";
import { TAG_GROUP_FRAGMENT } from "@neoledger/graphql/fragments";
import { EnumColors } from "@/constants/values";
import { ObjectType } from "@/objects/@types";
import { State } from "@/state";

/** A tag as a picker lists it: with the entity it belongs to. */
type TagOption = TagFragment & {
  entity?: { id: string; identifier: string } | null;
};

const object: ObjectType<TagFragment> = {
  humanName: "tag",
  inputKey: "tag",
  typename: "TagObject",
  report: {
    source: "tags",
    description: "The labels put on records to group them your own way",
    asks: "What has each project cost so far?",
  },
  ReactIcon: TagsIcon,
  titleFieldKey: "name",
  getTitle: (data) => {
    return data?.name || "";
  },
  getEntityId: (data) => {
    return (data as TagOption)?.entity?.id ?? null;
  },
  getReferenceValueOptions: (entityIdentifiers) => {
    return {
      // Every tag of the entities asked for: a field on one record passes its
      // entity, a filter every active entity. Read through the entity's tag
      // group, the list came up empty whenever that group had not loaded.
      query: gql`
        query TagList($entityIdentifiers: [String!]) {
          tags(entityIdentifiers: $entityIdentifiers) {
            data {
              ...Tag
              entity {
                id
                identifier
              }
            }
          }
        }
      `,
      queryOptions: {
        variables: {
          entityIdentifiers,
        },
      },
      prepareOptions(data: any) {
        return (data?.tags?.data || []).filter(
          (tag: any) => tag?.identifier != null,
        );
      },
      filterOptions(options: TagFragment[], filter: string) {
        return options.filter((option) => {
          return (option.name ?? "")
            .toLowerCase()
            .includes(filter.toLowerCase());
        });
      },
      mutationOptions: {
        // Optimistic inline create-and-assign for the tag picker.
        //
        // Flow (best case, ~0ms perceived latency):
        //   1. Build a synthetic ``TagFragment`` keyed on a
        //      client-generated UUID. Its ``id`` follows the real
        //      Relay encoding (``btoa("TagObject:<uuid>")``) so Apollo
        //      treats it as a normal cache entry.
        //   2. Call ``events.onChangeField([...currentValues, opt])``
        //      *synchronously*. This writes into the field's local
        //      Zustand store (no network) and the chip strip in
        //      ``FieldReferences`` repaints in the next frame.
        //   3. Fire ``createNode`` with an ``optimisticResponse`` that
        //      includes both the optimistic ``TagObject`` and the
        //      parent ``TagGroupObject`` with the appended tags list.
        //      Apollo writes both into its normalised cache, which the
        //      picker's option query reads from — so the new row
        //      *also* shows up immediately in the picker list (ticked,
        //      because ``getIsChecked`` consults ``currentValues``).
        //   4. When the real response lands, Apollo swaps refs in
        //      place. We then re-call ``onChangeField`` with the real
        //      tag (so ``submitNodeToGraphQLServer`` doesn't try to
        //      assign the parent with a temp UUID) and trigger
        //      ``onSubmitField`` to commit the parent ``UPDATE_NODE``.
        //   5. On error, roll the chip back to ``currentValues`` and
        //      surface a snackbar. Apollo automatically rolls back
        //      its optimistic cache write when the mutation rejects.
        //
        // Why split ``onChangeField`` from ``onSubmitField``: the
        // former is a pure local-state setter (see
        // ``Node.hooks.tsx#onChangeField``) — it only mutates the
        // Zustand store. The latter is what triggers
        // ``submitNodeToGraphQLServer``, which builds an
        // ``UPDATE_NODE`` payload referencing tag identifiers. Calling
        // ``onSubmitField`` with the optimistic UUID would 404 the
        // parent assignment, so we defer it until after the create
        // resolves with the real ID.
        onCreateNewOption: (
          filter,
          { events, currentValues, apolloClient },
        ) => {
          const trimmed = filter.trim();
          if (!trimmed) return null;

          const palette = [
            TagColorEnum.Aqua,
            TagColorEnum.Blue,
            TagColorEnum.Green,
            TagColorEnum.Lime,
            TagColorEnum.Orange,
            TagColorEnum.Pink,
            TagColorEnum.Purple,
            TagColorEnum.Violet,
            TagColorEnum.Yellow,
          ];
          const color = palette[currentValues.length % palette.length];

          const entityIdentifier = entityIdentifiers?.[0];
          const entity = State.getState().entities.find(
            (e) => e.identifier === entityIdentifier,
          );
          const tagGroupIdentifier = entity?.tagGroups?.[0]?.identifier;
          const tagGroupGlobalId = entity?.tagGroups?.[0]?.id;
          const tagGroupName = entity?.tagGroups?.[0]?.name ?? null;

          // Client-generated identifier so the optimistic and real
          // rows share an envelope; Apollo will replace the underlying
          // cache entry when the server responds with the canonical
          // (server-assigned) UUID.
          const tempIdentifier =
            typeof crypto !== "undefined" && "randomUUID" in crypto
              ? crypto.randomUUID()
              : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          const tempGlobalId = btoa(`TagObject:${tempIdentifier}`);

          const optimisticTag: TagFragment = {
            __typename: "TagObject",
            id: tempGlobalId,
            identifier: tempIdentifier,
            name: trimmed,
            color,
          };

          // 1. Local paint — chip appears in the multi-select strip
          //    in the next render (no await, no network).
          events?.onChangeField?.([...currentValues, optimisticTag]);

          // 2. Compute the optimistic TagGroup payload. We append the
          //    optimistic tag to whatever ``currentValues`` already
          //    held, *plus* any tags Apollo has cached for that group
          //    that aren't in the field's current selection. The
          //    field's ``currentValues`` only carries the assigned
          //    subset — we want the picker option list to render the
          //    full group's tags (assigned + unassigned).
          const cachedGroup = tagGroupGlobalId
            ? apolloClient.cache.readFragment<{
                __typename: "TagGroupObject";
                id: string;
                identifier: string;
                name?: string | null;
                tags: TagFragment[];
              }>({
                id: `TagGroupObject:${tagGroupGlobalId}`,
                fragment: TAG_GROUP_FRAGMENT,
                fragmentName: "TagGroup",
              })
            : null;
          const existingTags = cachedGroup?.tags ?? [];
          const optimisticGroup =
            tagGroupGlobalId && tagGroupIdentifier
              ? {
                  __typename: "TagGroupObject" as const,
                  id: tagGroupGlobalId,
                  identifier: tagGroupIdentifier,
                  name: cachedGroup?.name ?? tagGroupName,
                  tags: [...existingTags, optimisticTag],
                }
              : null;

          // 3. Fire the create. We don't await here — by returning the
          //    promise we let the List wrapper (``submitNewRow``)
          //    track in-flight state for double-submit guarding while
          //    the chip + picker UI have already painted.
          return apolloClient
            .mutate({
              mutation: Mutations.CREATE_NODE,
              variables: {
                input: {
                  entityIdentifier: entityIdentifier!,
                  node: {
                    tag: {
                      name: trimmed,
                      color,
                      ...(tagGroupIdentifier
                        ? { group: { identifier: tagGroupIdentifier } }
                        : {}),
                    },
                  },
                },
              },
              // The picker lists tags through ``TagList``; the new tag joins
              // it once the server has it.
              refetchQueries: ["TagList"],
              optimisticResponse: {
                createNode: {
                  __typename: "CreateNodePayload",
                  errors: null,
                  data: [
                    optimisticTag,
                    ...(optimisticGroup ? [optimisticGroup] : []),
                  ],
                },
              },
            })
            .then(({ data }) => {
              const real = data?.createNode?.data?.find(
                (n: { __typename?: string } | null | undefined) =>
                  n?.__typename === "TagObject",
              ) as TagFragment | undefined;
              if (!real) {
                // Server returned no tag — roll back the optimistic
                // chip rather than leaving the user with a row that
                // points at a non-existent ID.
                events?.onChangeField?.(currentValues);
                return null;
              }

              // 4. Swap optimistic → real, then commit the parent
              //    assignment (``UPDATE_NODE``). The chip in the strip
              //    re-renders with the canonical identifier; the
              //    server-side assignment uses that identifier so it
              //    resolves cleanly.
              events?.onChangeField?.([...currentValues, real]);
              events?.onSubmitField?.();
              return real;
            })
            .catch((err) => {
              // 5. Rollback. Apollo unwinds its own optimistic cache
              //    write; we just need to walk the field back to its
              //    pre-optimistic value.
              events?.onChangeField?.(currentValues);
              State.setState({
                snackbarMessage:
                  err instanceof Error
                    ? `Couldn't create tag: ${err.message}`
                    : "Couldn't create tag.",
              });
              return null;
            });
        },
      },
      onRenderOption: (option: TagOption) => {
        return (
          <div
            data-testid={`tag-option-${option.identifier}`}
            className="flex items-center gap-2 min-w-0"
          >
            <FieldEnum
              value={option.name}
              isMultiline={false}
              valueOptions={{
                enum: [
                  {
                    key: "name",
                    value: option.name,
                  },
                ],
                getColors(mode) {
                  return {
                    [option.name]: {
                      color: EnumColors[option.color]?.[mode]?.color,
                      backgroundColor:
                        EnumColors[option.color]?.[mode]?.backgroundColor,
                      borderColor:
                        EnumColors[option.color]?.[mode]?.borderColor,
                    },
                  };
                },
              }}
              style={{ paddingLeft: 0, paddingRight: 0 }}
            />
          </div>
        );
      },
      style: "enum",
    };
  },
  fieldDefinitions: [
    {
      objectKey: "name",
      label: "Name",
      type: "text",
      ReactIcon: TagsIcon,
      asString: (data) => data?.name || "",
      getValue: (data) => {
        return data?.name;
      },
      updateProps: {
        isRequired: true,
        isEditable: true,
      },
      createProps: {
        isRequired: true,
      },
      summaryProps: {
        isVisible: true,
      },
    },
  ],
};

export { object as TagObject };
